import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import _ from 'lodash';
import { Box, CircularProgress } from '@material-ui/core';
import { Search } from '..';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import { ehandoverAPI } from '../../../../apis';
import { getArrayRandomKey } from '../../../../utils';
import NewNote from './newNote';

const useStyles = makeStyles(() => ({
  note: {
    '&>div:first-child': {
      borderRadius: '0 0 0.9375rem 0.9375rem',
      '&>div:first-child': {
        borderRadius: '0 0 0.9375rem 0.9375rem',
      },
    },
  },
}));

const initSearch = (search) => (search === '' ? null : search);

const initTag = (tag) => (tag.length > 0 ? tag : null);
const EMPTY_TASK_CHECKLIST = [];

export default function OldNewNotes({
  notes, // it is the core list
  wardStaffdata,
  type,
  getTaskListAPI,
  next,
  deleteTaskAPI,
  pinTaskAPI,
  search,
  setSearch,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  tagList,
  selectedTags,
  setSelectedTags,
  filterAPI,
  followUpFilter,
  handleFollowUpFilter,
  followUpTotal,
  refreshChecklistTabs,
  handleReset,
  sortOrder,
  setSortOrder,
  quickDays,
  setQuickDays,
  accessControl,
  individualUsers,
  setPendingAction,
  staffList,
  requiredAcknowLedge,
  setRequiredAcknowLedge,
  setHideCompletedItem,
  isHideCompletedItem,
  showHideCompleteItem
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [detailNotesList, setDetailNotesList] = useState([]);
  const nextPageFlagRef = useRef(false);
  const getRequestParamsRef = useRef(null);
  const getTaskListAPIRef = useRef(getTaskListAPI);
  const filterAPIRef = useRef(filterAPI);
  const refreshChecklistTabsRef = useRef(refreshChecklistTabs);
  const deleteTaskAPIRef = useRef(deleteTaskAPI);
  const pinTaskAPIRef = useRef(pinTaskAPI);
  const typeRef = useRef(type);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  const loginInfo = useSelector((state) => (state.loginInfo));
  const userRole = useSelector((state) => (state.userRole));
  const sinkCompletedToBottom = useSelector((state) => (state.ehdSetting.sinkCompletedChecklist));

  getTaskListAPIRef.current = getTaskListAPI;
  filterAPIRef.current = filterAPI;
  refreshChecklistTabsRef.current = refreshChecklistTabs;
  deleteTaskAPIRef.current = deleteTaskAPI;
  pinTaskAPIRef.current = pinTaskAPI;
  typeRef.current = type;
  startDateRef.current = startDate;
  endDateRef.current = endDate;

  const handleVerification = (start, end) => {
    return Boolean(
      (moment(start, '20yy-MM-DD HH:mm', true).isValid() &&
        moment(end, '20yy-MM-DD HH:mm', true).isValid() &&
        moment(end).isSameOrAfter(start, 'days')) ||
        (moment(start, '20yy-MM-DD HH:mm', true).isValid() && !moment(end, '20yy-MM-DD HH:mm', true).isValid()) ||
        (!moment(start, '20yy-MM-DD HH:mm', true).isValid() && moment(end, '20yy-MM-DD HH:mm', true).isValid())
    );
  };

  // 统一整理当前 tab 请求参数，避免首屏加载、搜索和分页各自拼一遍。
  const getRequestParams = useCallback(() => {
    return getRequestParamsRef.current?.({}) ?? {};
  }, []);

  // 支持在 search 输入框里先本地编辑，只有点击搜索/回车时才把关键词提交到页面层。
  const createRequestParams = useCallback(
    ({
      searchValue = search,
      selectedTagsValue = selectedTags,
      startDateValue = startDate,
      endDateValue = endDate,
    } = {}) => {
      const tags = _.map(selectedTagsValue, (item) => item?.tagName);
      let params = {
        search: initSearch(searchValue),
        tag: initTag(tags),
      };

      if (handleVerification(startDateValue, endDateValue)) {
        params.startDate = startDateValue;
        params.endDate = endDateValue;
      }

      return params;
    },
    [endDate, search, selectedTags, startDate]
  );
  getRequestParamsRef.current = createRequestParams;
  const loadCurrentTabList = useCallback(async () => {
    await getTaskListAPIRef.current(getRequestParams());
  }, [getRequestParams]);

  // 当前 tab 首次挂载时，只加载当前 tab 自己的 list。
  useEffect(() => {
    if (handleVerification(startDate, endDate)) {
      (async () => {
        await loadCurrentTabList();
      })();
    }
  }, []);

  // 公共筛选变化后，走页面级 filterAPI，由页面决定“当前 list + 哪些 total 需要联动刷新”。
  useUpdateEffect(async () => {
    if (handleVerification(startDate, endDate)) {
      (async () => {
        await handleSearch();
      })();
    }
  }, [startDate, endDate, sortOrder, selectedTags, requiredAcknowLedge]);

  // 通过 ref 读取最新筛选条件，避免筛选输入变化时每条 NewNote 都因为回调引用变化重渲染。
  const updateFollowTotal = useCallback(() => {
    if (!refreshChecklistTabsRef.current) {
      return;
    }
    const params = getRequestParamsRef.current();
    if (handleVerification(startDateRef.current, endDateRef.current)) {
      refreshChecklistTabsRef.current({
        ...params,
        startDate: startDateRef.current,
        endDate: endDateRef.current,
      });
    } else {
      refreshChecklistTabsRef.current(params);
    }
  }, []);

  const handleSearch = useCallback(async (start) => {
    const isLoadMore = Boolean(start);
    if (isLoadMore && nextPageFlagRef.current) {
      return;
    }
    try {
      setLoading(true);
      let params = getRequestParamsRef.current();

      // 分页时只加载当前 tab 的下一页，不触发其它 tab 的 total / list 刷新。
      if (isLoadMore) {
        nextPageFlagRef.current = true;
        params.start = start;
        await getTaskListAPIRef.current(params);
        return;
      }

      await filterAPIRef.current(params);
    } catch (error) {
    } finally {
      if (isLoadMore) {
        nextPageFlagRef.current = false;
      }
      setLoading(false);
    }
  }, []);

  const handleSubmitSearch = useCallback(
    async (nextSearch) => {
      const searchValue = nextSearch ?? '';
      setSearch(searchValue);
      try {
        setLoading(true);
        await filterAPIRef.current(getRequestParamsRef.current({ searchValue }));
      } catch (error) {
      } finally {
        setLoading(false);
      }
    },
    [setSearch]
  );

  // followUpFilter 只影响 checklist 当前 tab，保持原来的单 tab 刷新逻辑。
  const searchFollowUp = useCallback(async () => {
    try {
      await getTaskListAPIRef.current(getRequestParamsRef.current());
    } catch (error) {
      console.log(error);
    }
  }, []);

  useUpdateEffect(async () => {
    (async () => {
      await searchFollowUp();
    })();
  }, [followUpFilter]);

  const handleSearchRef = useRef(handleSearch);
  handleSearchRef.current = handleSearch;

  const handlePin = useCallback(({ taskId, pin, isManualUnpin, pinExpiryDate }) => {
    pinTaskAPIRef.current({
      taskId,
      pin,
      reLoad: handleSearchRef.current,
      isManualUnpin,
      pinExpiryDate,
    });
  }, []);

  const handleDelete = useCallback((TASK_ID, name, deleteOptions) => {
    deleteTaskAPIRef.current(TASK_ID, name, handleSearchRef.current, typeRef.current, deleteOptions);
  }, []);

  useEffect(() => {
    // console.log("useEffect 3");

    const root = document.getElementById('ehandover-root');
    if (!root) return;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = root;
      // 简化判断：只要距离底部 5px 以内就认为触底
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        if (!loading && next) {
          (async () => {
            await handleSearchRef.current(next);
          })();
        }
      }
    };

    root.addEventListener('scroll', onScroll);
    return () => {
      root.removeEventListener('scroll', onScroll);
    };
  }, [next, loading]);

  // new get noteList API
  const getNewTaskItemListAPI = useCallback(async function fetchTaskItemList(
    apiTaskIdList, // List of task IDs
    order = { ID: 'asc', TOGGLED_DATE: 'asc', IS_COMPLETE: 'desc', SORT_INDEX: 'asc' },
    retries = 3,
    delay = 1000 // Initial delay for retries
  ) {
    if (_.isEmpty(apiTaskIdList)) {
      return;
    }
    try {
      const { result } = await ehandoverAPI.getNewTaskItemList(
        { order, taskIdList: apiTaskIdList },
        { isHideLoading: true }
      );
      const groupList = result.reduce((acc, item) => {
        const key = item.TASK_ID;
        acc[key] = acc[key] || [];
        acc[key].push(item);
        return acc;
      }, {});
      setDetailNotesList((item) => ({ ...item, ...groupList }));
    } catch (error) {
      console.error('API call failed:', error);
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchTaskItemList(apiTaskIdList, order, retries - 1, delay * 2);
      }
      console.error('Failed to fetch task item list after maximum retries');
    }
  }, []);

  const fetchTaskItems = useMemo(
    () =>
      _.debounce(async (arr) => {
        try {
          await getNewTaskItemListAPI(arr);
        } catch (error) {}
      }, 300),
    [getNewTaskItemListAPI]
  );

  useEffect(() => {
    return () => {
      fetchTaskItems.cancel();
    };
  }, [fetchTaskItems]);

  useEffect(() => {
    if (!_.isEmpty(notes)) {
      const slicedNotes = notes.slice(Math.max(0, notes.length - 50), notes.length);
      const taskIds = slicedNotes.map((e) => Number(e.TASK_ID));

      fetchTaskItems(taskIds);
    }
  }, [notes, fetchTaskItems]);

  return (
    <div>
      <Search
        handleSubmitSearch={handleSubmitSearch}
        search={search}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        type={type}
        tagList={tagList}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        followUpFilter={followUpFilter}
        handleFollowUpFilter={handleFollowUpFilter}
        followUpTotal={followUpTotal}
        handleReset={handleReset}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        quickDays={quickDays}
        setQuickDays={setQuickDays}
        requiredAcknowLedge={requiredAcknowLedge}
        setRequiredAcknowLedge={setRequiredAcknowLedge}
        setHideCompletedItem={setHideCompletedItem}
        isHideCompletedItem={isHideCompletedItem}
        showHideCompleteItem={showHideCompleteItem}
      />
      {loading && notes.length === 0 ? (
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" p="1.25rem 0">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <div className={classes.note}>
          {notes.map((item, index) => {
            const taskCheckList = detailNotesList[item.TASK_ID] ?? EMPTY_TASK_CHECKLIST;
            return (
              <NewNote
                key={getArrayRandomKey(index, `new_notes_${item?.TASK_ID ?? ''}}`)}
                id={index + type}
                wardStaffdata={wardStaffdata}
                deleteTaskAPI={handleDelete}
                pinTaskAPI={handlePin}
                {...item}
                type={type}
                handleSearch={handleSearch}
                itemData={item.metadata}
                updateFollowTotal={updateFollowTotal}
                taskCheckList={taskCheckList}
                getNewTaskItemListAPI={getNewTaskItemListAPI}
                accessControl={accessControl}
                individualUsers={individualUsers}
                setPendingAction={setPendingAction}
                staffList={staffList}
                loginInfo={loginInfo}
                userRole={userRole}
                sinkCompletedToBottom={sinkCompletedToBottom}
                isHideCompletedItem={isHideCompletedItem}
              />
            );
          })}
          {loading && (
            <Box width="100%" display="flex" justifyContent="center" alignItems="center" p="1.25rem 0">
              <CircularProgress color="primary" />
            </Box>
          )}
        </div>
      )}
    </div>
  );
}
